import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { shallowEqual } from 'hooks/reduxHooks'
import { rem } from 'polished'
import type { ComponentProps, PropsWithChildren, ReactElement, Ref } from 'react'
import { memo } from 'react'
import styled from 'styled-components'
import TextButton from '../Button/TextButton'
import BodyTextBlock from '../TextBlocks/BodyTextBlock'

const BannerSheet = styled.div`
  &.kind-informational-primary {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    color: ${props => props.theme.palette.neutral.surface.lightContrast};
  }
  &.kind-informational-secondary {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
  &.kind-promotional-primary {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    color: ${props => props.theme.palette.highlight.primary.lightContrast};
  }
  &.kind-promotional-primary-accent {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.highlight.primary.normalContrast};
  }
  &.kind-promotional-secondary {
    background-color: ${props => props.theme.palette.highlight.tertiary.lightBackground};
    color: ${props => props.theme.palette.highlight.tertiary.lightContrast};
  }
  &.kind-success {
    background-color: ${props => props.theme.palette.messaging.success.lightBackground};
    color: ${props => props.theme.palette.messaging.success.lightContrast};
  }
  &.kind-warning {
    background-color: ${props => props.theme.palette.messaging.warning.lightBackground};
    color: ${props => props.theme.palette.messaging.warning.lightContrast};
  }
  &.kind-critical {
    background-color: ${props => props.theme.palette.messaging.critical.lightBackground};
    color: ${props => props.theme.palette.messaging.critical.lightContrast};
  }
`

const BannerContainer = styled.div`
  display: grid;

  &.align-start {
    grid-template:
      "content ." auto
      "actions actions" auto / auto 1fr;
  }
  &.align-center {
    grid-template:
      ". content ." auto
      "actions actions actions" auto / 1fr auto 1fr;
  }

  &.density-comfortable {
    padding: ${rem(16)};
  }
  &.density-compact {
    padding: ${rem(12)} ${rem(16)};
  }

  ${mediaQueryUp.tablet} {
    align-items: center;

    &.align-on-tablet-start {
      grid-template: "content . actions" auto / auto 1fr auto;
    }
    &.align-on-tablet-center {
      grid-template: ". content actions ." auto / 1fr auto auto 1fr;
    }
  }
`

const BannerContent = styled(BodyTextBlock)`
  grid-area: content;
`

const BannerActionsGroup = styled(Group)`
  grid-area: actions;
  margin: ${rem(8)} 0 0 0;

  ${mediaQueryUp.tablet} {
    margin: 0;
    margin-inline-start: ${rem(12)};
  }
`

export type MessageBannerKind =
  | 'informational-primary'
  | 'informational-secondary'
  | 'promotional-primary'
  | 'promotional-primary-accent'
  | 'promotional-secondary'
  | 'success'
  | 'warning'
  | 'critical'

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  ref?: Ref<HTMLDivElement>
  /** sets the colour of the banner */
  kind: MessageBannerKind
  /**
   * sets the horizontal alignment of the content
   * @default 'start'
   * */
  align?: 'start' | 'center'
  /**
   * sets the horizontal alignment of the content on tablet breakpoint and higher
   * @default falls back to `align`
   * */
  tabletAlign?: 'start' | 'center'
  /**
   * sets the padding of the banner container
   *
   * @default comfortable
   * */
  density?: 'comfortable' | 'compact'
  /** sets the icon at the start of the description */
  icon?: ReactElement | false
  /** sets the textual content that sits above the description as a heading */
  heading?: string | ReactElement
  className?: string
  id?: string
  /**
   * sets the props passed into the primary action
   *  */
  primaryActionButtonProps?: Omit<ComponentProps<typeof TextButton>, 'kind' | 'variant'>
  /**
   * sets the props passed into the secondary action
   *  */
  secondaryActionButtonProps?: Omit<ComponentProps<typeof TextButton>, 'kind' | 'variant'>
}

function MessageBanner({
  children,
  kind,
  align = 'start',
  tabletAlign,
  density = 'comfortable',
  heading,
  icon,
  className,
  primaryActionButtonProps,
  secondaryActionButtonProps,
  ...rest
}: Props) {
  return <BannerSheet
    {...rest}
    className={clsx(
      className,
      `kind-${kind}`,
    )}
  >
    <BannerContainer
      className={clsx(
      `align-${align}`,
      `align-on-tablet-${tabletAlign ?? align}`,
      `density-${density}`,
      )}
    >
      <BannerContent
        startIcon={icon}
        variant="medium"
        align={align}
        tabletAlign={tabletAlign}
      >
        <Group direction="vertical" gap={4}>
          {!!heading && <b>{heading}</b>}
          <div>
            {children}
          </div>
        </Group>
      </BannerContent>

      {(primaryActionButtonProps || secondaryActionButtonProps) && <BannerActionsGroup
        direction="horizontal"
        horizontalAlign="end"
        gap={8}
      >
        {secondaryActionButtonProps && <TextButton {...secondaryActionButtonProps} kind="tertiary" />}
        {primaryActionButtonProps && <TextButton {...primaryActionButtonProps} kind="secondary" />}
      </BannerActionsGroup>}
    </BannerContainer>
  </BannerSheet>
}

export default memo(MessageBanner, (prevProps, nextProps) => {
  const {
    primaryActionButtonProps: prevPrimaryActionButtonProps,
    secondaryActionButtonProps: prevSecondaryActionButtonProps,
    ...prevRest
  } = prevProps
  const {
    primaryActionButtonProps: nextPrimaryActionButtonProps,
    secondaryActionButtonProps: nextSecondaryActionButtonProps,
    ...nextRest
  } = nextProps

  return shallowEqual(prevRest, nextRest) &&
    shallowEqual(prevPrimaryActionButtonProps, nextPrimaryActionButtonProps) &&
    shallowEqual(prevSecondaryActionButtonProps, nextSecondaryActionButtonProps)
})
